<!-- src/views/legal/PrivacyPolicy.vue -->
<!-- TEMPLATE — have counsel review before production reliance. -->

<script setup lang="ts">
  import { RouterLink } from 'vue-router';

  // Fixed publication date. Do NOT compute at runtime (no Date.now) so the
  // "Last updated" string is stable and auditable across renders/deploys.
  const lastUpdated = 'July 6, 2026';

  // Shared prose classes, kept in the script so the template stays readable.
  const h2 = 'mt-12 mb-3 text-xl font-semibold text-gray-900 dark:text-gray-100';
  const h3 = 'mt-6 mb-2 text-base font-semibold text-gray-800 dark:text-gray-200';
  const p = 'mb-4 text-base leading-7 text-gray-600 dark:text-gray-300';
  const ul = 'mb-4 ml-5 list-disc space-y-2 text-base leading-7 text-gray-600 dark:text-gray-300';
  const a =
    'font-medium text-brand-600 underline underline-offset-2 hover:text-brand-700 dark:text-brand-400 dark:hover:text-brand-300';
</script>

<template>
  <div class="container mx-auto max-w-3xl px-4 py-8">
    <article aria-labelledby="privacy-title">
      <header>
        <h1
          id="privacy-title"
          class="text-3xl font-bold tracking-tight text-gray-900 dark:text-gray-100">
          Privacy Policy
        </h1>
        <p class="mt-2 text-sm text-gray-500 dark:text-gray-400">
          Last updated: {{ lastUpdated }}
        </p>
      </header>

      <p :class="[p, 'mt-8']">
        BurnLink lets you share sensitive information through links that self-destruct after a
        single view or when they expire. This policy explains what we collect, why, how long we
        keep it, and the choices you have. We designed the service to collect as little as
        possible — most of what you share, we deliberately cannot read.
      </p>

      <h2 :class="h2">Information we collect</h2>

      <h3 :class="h3">Secret content</h3>
      <p :class="p">
        When you create a secret, its content is encrypted before it is stored. We hold only the
        resulting ciphertext — not your plaintext, and not the key needed to read it in a usable
        form. As a result,
        <strong class="font-semibold text-gray-800 dark:text-gray-200">
          we cannot read the secrets you share</strong>, cannot recover them once they are burned,
        and cannot produce their contents in response to a request. If you protect a secret with a
        passphrase, that passphrase is stored only as a one-way hash (see our
        <RouterLink :class="a" to="/security">Security</RouterLink> page).
      </p>

      <h3 :class="h3">Account information</h3>
      <p :class="p">
        You can use BurnLink to create and view secrets without an account. If you choose to sign
        up, we store your email address and authentication metadata (for example, a hashed
        credential or magic-link token) so you can sign in and manage your links. We do not require
        a name, phone number, or payment details to use the core service.
      </p>

      <h3 :class="h3">Technical and log data</h3>
      <p :class="p">
        To operate the service securely we keep minimal operational logs — such as coarse request
        metadata, error diagnostics, and abuse-prevention signals. We do not log secret content,
        passphrases, or the plaintext of anything you share. Where IP addresses appear in transient
        logs, we mask or minimize them and retain them only briefly for security and rate limiting.
        We do not sell your data, and we do not use it for advertising or profiling.
      </p>

      <h2 :class="h2">How we use information</h2>
      <ul :class="ul">
        <li>To deliver the core function: creating, encrypting, storing, and revealing secrets.</li>
        <li>To authenticate you and manage your account, if you have one.</li>
        <li>To send transactional email you request or that is required to operate your account.</li>
        <li>To keep the service secure — preventing abuse, fraud, and automated attacks.</li>
        <li>To diagnose errors and maintain reliability.</li>
      </ul>

      <h2 :class="h2">Data retention</h2>
      <p :class="p">
        Secrets are ephemeral by design. A secret is
        <strong class="font-semibold text-gray-800 dark:text-gray-200">
          permanently deleted the first time it is viewed</strong>, or automatically at the end of
        its expiration window (its time-to-live), whichever comes first. Nothing about a secret's
        content is retained past its TTL. Once a secret is burned or expires, it is gone and cannot
        be restored by you or by us.
      </p>
      <p :class="p">
        Automated link previews, security scanners, or whoever opens the link first can consume
        it before your intended recipient does — share one-time links thoughtfully.
      </p>
      <p :class="p">
        Account records persist until you delete your account. Minimal security and operational logs
        are retained only for as long as needed for the purpose that created them, then discarded.
      </p>

      <h2 :class="h2">Cookies and sessions</h2>
      <p :class="p">
        We use a small number of strictly necessary cookies to keep you signed in, protect against
        cross-site request forgery, and remember interface preferences such as language and theme.
        We do not use third-party advertising or cross-site tracking cookies.
      </p>

      <h2 :class="h2">Sub-processors</h2>
      <p :class="p">
        We run BurnLink on a small set of infrastructure providers who process data on our behalf,
        under their own security and privacy terms:
      </p>
      <ul :class="ul">
        <li>
          <strong class="font-semibold text-gray-800 dark:text-gray-200">Fly.io</strong> —
          application hosting and compute for the service.
        </li>
        <li>
          <strong class="font-semibold text-gray-800 dark:text-gray-200">Cloudflare</strong> —
          edge network, CDN, DNS, and DDoS/abuse protection in front of the application.
        </li>
        <li>
          <strong class="font-semibold text-gray-800 dark:text-gray-200">
            Transactional email provider</strong> — delivery of account and notification emails
          only. Secret content is never sent through email.
        </li>
      </ul>

      <h2 :class="h2">International data transfers</h2>
      <p :class="p">
        Our infrastructure providers operate globally, so your data may be processed in countries
        other than your own. Where information moves across borders, we rely on the safeguards
        offered by those providers. If you have specific data-residency requirements, contact us
        before relying on the service.
      </p>

      <h2 :class="h2">Your rights</h2>
      <p :class="p">
        Depending on where you live, you may have the right to access, correct, export, or delete
        the personal data we hold about you, and to object to or restrict certain processing. You
        can delete your account at any time. To exercise any of these rights, email
        <a :class="a" href="mailto:privacy@burnlink.app">privacy@burnlink.app</a>. Note that,
        because we cannot read secret content, we generally cannot retrieve or reproduce the
        contents of a secret — even at your request.
      </p>

      <h2 :class="h2">Children</h2>
      <p :class="p">
        BurnLink is not directed to children and is not intended for use by anyone under the age of
        digital consent in their jurisdiction. We do not knowingly collect personal data from
        children.
      </p>

      <h2 :class="h2">Changes to this policy</h2>
      <p :class="p">
        We may update this policy as the service evolves. When we make material changes, we will
        revise the "Last updated" date above and, where appropriate, provide additional notice.
      </p>

      <h2 :class="h2">Contact</h2>
      <p :class="p">
        Questions about privacy? Email
        <a :class="a" href="mailto:privacy@burnlink.app">privacy@burnlink.app</a>. See also our
        <RouterLink :class="a" to="/terms">Terms of Service</RouterLink> and
        <RouterLink :class="a" to="/security">Security</RouterLink> pages.
      </p>
    </article>
  </div>
</template>
